import React from "react";
import { cn } from "@/lib/utils";

export type HowItWorksStep = {
  icon: any;
  title: string;
  desc: string;
};

interface HowItWorksProps {
  title: React.ReactNode;
  steps: HowItWorksStep[];
  stepInView?: boolean[];
  sectionClassName?: string;
}

export const HowItWorks: React.FC<HowItWorksProps> = ({
  title,
  steps,
  stepInView,
  sectionClassName,
}) => {
  return (
    <section
      className={cn(
        "py-20 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white px-4 md:px-8 lg:px-16 relative overflow-hidden",
        sectionClassName
      )}
    >
      <div className="max-w-7xl mx-auto relative z-10">
        <h2 className="text-3xl md:text-4xl font-extrabold mb-16 text-center">{title}</h2>
        <div className="relative">
          <div className="relative flex flex-col md:flex-row items-center md:items-start justify-center md:justify-between gap-12">
            {steps.map((step, i) => (
              <div
                key={i}
                className={cn(
                  "md:flex-1 w-full md:w-auto mx-auto flex flex-col items-center text-center relative transition-all duration-500 ease-in-out",
                  stepInView && stepInView[i]
                    ? "opacity-100 translate-y-0"
                    : stepInView
                    ? "opacity-0 translate-y-10"
                    : ""
                )}
              >
                {/* Step Number */}
                <div className="w-16 h-16 rounded-full border-4 border-dashed border-primary-400 dark:border-primary-600 flex items-center justify-center mb-6 relative z-10 bg-white dark:bg-slate-800">
                  <div className="w-12 h-12 rounded-full bg-primary-500 dark:text-white flex items-center justify-center text-slate-900 font-semibold text-lg">
                    {i + 1}
                  </div>
                </div>

                {/* Icon */}
                <div className="w-20 h-20 rounded-full bg-slate-700 text-primary-500 dark:text-primary-400 flex items-center justify-center text-3xl mb-4 shadow-md border-2 border-slate-200 dark:border-slate-600">
                  <img className='w-10 h-10' src={step.icon} alt={step.title} />
                </div>

                <h3 className="text-lg font-bold mb-2 text-slate-800 dark:text-white">{step.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 max-w-xs">{step.desc}</p>

                {/* Dotted Arrow to next step */}
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-[72px] left-full w-24 h-2 -translate-x-1/2">
                    <div className="w-full h-full flex items-center">
                      <div className="h-0.5 w-full bg-[length:16px_2px] bg-[linear-gradient(90deg,#64748b_50%,transparent_50%)] bg-repeat-x dark:bg-[linear-gradient(90deg,#94a3b8_50%,transparent_50%)]" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
